import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "../../assets/icons";

function getScrollParent(node) {
  if (!node) return null;
  let el = node.parentElement;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(overflowY)) return el;
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

const NavBarMission = ({ user, isWaiting, onCompactChange }) => {
  const location = useLocation();
  const rootRef = useRef(null);
  const [compact, setCompact] = useState(false);

  const params = new URLSearchParams(location.search);

  const dateStartParam = params.get("dateStart");
  const dateEndParam = params.get("dateEnd");
  const searchParam = params.get("search");
  const companySelectedParam = params.get("companySelected");
  const interimAgencySelectedParam = params.get("interimAgencySelected");
  const interimSectorSelectedParam = params.get("interimSectorSelected");
  const missionStatusListParam = params.get("missionStatusList");

  const paramsToPass = new URLSearchParams();

  if (dateStartParam) paramsToPass.append("dateStart", dateStartParam);
  if (dateEndParam) paramsToPass.append("dateEnd", dateEndParam);
  if (searchParam) paramsToPass.append("search", searchParam);
  if (companySelectedParam)
    paramsToPass.append("companySelected", companySelectedParam);
  if (interimAgencySelectedParam)
    paramsToPass.append("interimAgencySelected", interimAgencySelectedParam);
  if (interimSectorSelectedParam)
    paramsToPass.append("interimSectorSelected", interimSectorSelectedParam);
  if (missionStatusListParam)
    paramsToPass.append("missionStatusList", missionStatusListParam);

  const navLinkUrl = `/hourly-tracking?${paramsToPass.toString()}`;

  /** Hystérésis : évite de basculer compact / normal en boucle près du seuil (sauts de layout + ResizeObserver). */
  const SCROLL_COMPACT_ON = 48;
  const SCROLL_COMPACT_OFF = 12;

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const scrollParent = getScrollParent(node);
    if (!scrollParent) return;

    const readScrollTop = () =>
      scrollParent === document.documentElement ||
      scrollParent === document.scrollingElement
        ? window.scrollY
        : scrollParent.scrollTop;

    const onScroll = () => {
      const y = readScrollTop();
      setCompact((prev) => {
        if (!prev && y >= SCROLL_COMPACT_ON) return true;
        if (prev && y <= SCROLL_COMPACT_OFF) return false;
        return prev;
      });
    };

    onScroll();
    scrollParent.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollParent.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    onCompactChange?.(compact);
  }, [compact, onCompactChange]);

  return (
    <nav
      ref={rootRef}
      className={["w-full bg-white", compact ? "py-2" : "py-3"].join(" ")}
    >
      <div className={"flex w-full items-center justify-between"}>
        <div className={"flex items-center gap-3 sm:gap-4"}>
          <NavLink
            to={navLinkUrl}
            className={[
              "inline-flex shrink-0 items-center justify-center rounded-full border",
              compact ? "p-2" : "p-3",
            ].join(" ")}
            aria-label="Retour au suivi horaire"
          >
            <ArrowLeft wh={compact ? 20 : 24} color={"#374151"} />
          </NavLink>
          <div className={"flex min-w-0 flex-col"}>
            <div
              className={[
                "truncate font-bold capitalize",
                compact ? "text-base leading-snug" : "text-xl leading-tight sm:text-2xl",
              ].join(" ")}
            >
              {isWaiting ? (
                <div
                  className={[
                    "rounded bg-gray-200",
                    compact ? "h-5 w-32" : "h-7 w-40",
                  ].join(" ")}
                />
              ) : (
                `${user?.firstname} ${user?.lastname}`
              )}
            </div>
            <div
              className={[
                "text-gray-500",
                compact ? "text-xs" : "text-sm",
              ].join(" ")}
            >
              {isWaiting ? (
                <div
                  className={[
                    "mt-1 rounded bg-gray-200",
                    compact ? "h-3 w-16" : "h-4 w-20",
                  ].join(" ")}
                />
              ) : (
                (() => {
                  const matricules = Array.isArray(user?.matricules)
                    ? user.matricules
                    : user?.peopulseId
                      ? [{ source: "peopulse", value: user.peopulseId }]
                      : [];
                  if (matricules.length === 0) return "";
                  return matricules
                    .map((m) => {
                      const value = m.value || m.externalId;
                      const sourceLabel =
                        m.source === "peopulse"
                          ? "Peopulse"
                          : m.source === "jobbiz"
                            ? "Jobbiz"
                            : m.source === "workfully"
                              ? "Workfully"
                              : (m.source || "").charAt(0).toUpperCase() +
                                (m.source || "").slice(1);
                      return `${sourceLabel} : ${value}`;
                    })
                    .join(" • ");
                })()
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBarMission;
